/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/hooks/useOrgFilter.ts
 * DESCRIPTION: Returns the active org_id to pass as a filter parameter
 *              to API calls, based on the current workspace scope.
 *
 * LOGIC:
 *   - Global scope  → returns undefined (no filter, see everything)
 *   - Personal scope → returns the personal org's ID
 *   - Org scope      → returns the active team org's ID
 *
 * USAGE:
 *   const orgFilter = useOrgFilter();
 *   const data = await apiClient.listIdentities({ org_id: orgFilter });
 *   await apiClient.uploadFile(file, { org_id: orgFilter });
 *
 * ROADMAP: Phase 2.3 — Scope-Aware Frontend
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-09-05
 * ─────────────────────────────────────────────────────────────────────
 */

import { useWorkspace } from '../contexts/WorkspaceContext';

/**
 * Returns the org_id to use as a filter for API calls,
 * or undefined if the current scope is 'global' (no filtering).
 */
export const useOrgFilter = (): string | undefined => {
  const { scope, activeOrgId } = useWorkspace();

  // Global scope = no org filter (platform-wide view)
  if (scope === 'global') return undefined;

  // Personal or Organization scope = filter by active org
  return activeOrgId || undefined;
};
